import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShoppingCart, UploadSimple, FileXls, Storefront, Trash } from "@phosphor-icons/react";

export default function POUploadTab({ refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/purchase-orders/summary");
      setSummary(r.data || null);
    } catch {
      toast.error("Failed to load PO summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const r = await api.post("/uploads/purchase_orders", form, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${f.name} · ${r.data?.inserted ?? 0} new + ${r.data?.updated ?? 0} updated POs · ${fmtINR(r.data?.total_value || 0)}`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    load();
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL uploaded POs? This cannot be undone.")) return;
    try {
      const r = await api.delete("/purchase-orders");
      toast.success(`Deleted ${r.data?.deleted || 0} POs`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const outlets = summary?.outlets || [];

  return (
    <div data-testid="po-upload-tab" className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
              <ShoppingCart size={15} weight="duotone" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">GRN upload</h2>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            Drop PetPooja purchase-order XLSX exports (one per outlet). POs are grouped by PO Number and matched into the
            Outstanding tab next to each vendor.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="po-file-input"
            disabled={busy}
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="h-9 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-cyan-50"
            data-testid="po-upload-btn"
          >
            <UploadSimple size={14} weight="bold" className="mr-1.5" />
            {busy ? `Uploading ${progress.done}/${progress.total}…` : "Upload PO file(s)"}
          </Button>
          {summary?.total_pos > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={clearAll}
              disabled={busy}
              className="h-9 rounded-sm border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="po-clear-btn"
            >
              <Trash size={14} weight="bold" className="mr-1.5" />
              Clear all
            </Button>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-md border border-cyan-900/40 bg-slate-900/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total POs</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-cyan-100 mt-1">{summary?.total_pos ?? "—"}</div>
        </div>
        <div className="rounded-md border border-cyan-900/40 bg-slate-900/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total PO value</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-cyan-100 mt-1">{fmtINR(summary?.total_value || 0)}</div>
        </div>
        <div className="rounded-md border border-cyan-900/40 bg-slate-900/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Outlets</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-cyan-100 mt-1">{outlets.length}</div>
        </div>
      </div>

      {/* Per-outlet breakdown */}
      <div className="rounded-md border border-cyan-900/40 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="po-outlets-table">
            <thead>
              <tr className="bg-slate-900/70 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                <th className="text-left px-4 py-3">Outlet</th>
                <th className="text-right px-4 py-3">POs</th>
                <th className="text-right px-4 py-3">Total Value</th>
                <th className="text-left px-4 py-3">Earliest</th>
                <th className="text-left px-4 py-3">Latest</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
              )}
              {!loading && outlets.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center">
                    <FileXls size={32} weight="duotone" className="text-cyan-700 inline-block mb-2" />
                    <div className="text-sm text-slate-300 font-semibold">No POs uploaded yet</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Drop your PetPooja PO XLSX files above to get started.
                    </div>
                  </td>
                </tr>
              )}
              {!loading && outlets.map((o, i) => (
                <tr key={i} className="border-t border-cyan-900/30 hover:bg-slate-900/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Storefront size={13} weight="duotone" className="text-cyan-300 shrink-0" />
                      <span className="font-semibold text-cyan-50">{o.outlet || "(unknown)"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-200">{o.po_count}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums font-bold text-cyan-100">{fmtINR(o.value)}</td>
                  <td className="px-4 py-3 text-slate-300 font-mono tabular-nums">{o.earliest ? fmtDate(o.earliest) : "—"}</td>
                  <td className="px-4 py-3 text-slate-300 font-mono tabular-nums">{o.latest ? fmtDate(o.latest) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
